import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { MediaImage } from 'editor/src/store/design/types';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import { addImageAction } from 'editor/src/store/gallery/slice';
import { ImageState, MimeType } from 'editor/src/store/gallery/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import loadImageDimensions from 'editor/src/util/loadImageDimensions';
import { SetTrimmedAssetData } from 'editor/src/util/postMessages/messages';

import updateMediaElementByUuidOperation from './updateMediaElementByUuidOperation';

const setTrimmedAssetOperation =
  (data: SetTrimmedAssetData) => async (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();

    // Get original image to preserve its properties (type, etc.)
    const originalImage = getImageById(state, data.originalImageId);
    const imageType = originalImage?.type || MimeType.PNG; // Default to PNG if original not found

    // Find all elements with the original imageId
    const elementsWithImageId: Array<{
      element: MediaImage;
      spreadIndex: number;
      pageIndex: number;
      elementIndex: number;
    }> = [];

    state.design.designData?.spreads.forEach((spread, spreadIndex) => {
      spread.pages.forEach((page, pageIndex) => {
        page.groups.media?.forEach((element, elementIndex) => {
          if (element.type === 'image' && element.imageId === data.originalImageId) {
            elementsWithImageId.push({
              element: element as MediaImage,
              spreadIndex,
              pageIndex,
              elementIndex,
            });
          }
        });
      });
    });

    if (elementsWithImageId.length === 0) {
      // Still add the trimmed image to gallery for potential future use
      try {
        const imageDimensions = await loadImageDimensions(data.newImageUrl, undefined, {
          executor: 'setTrimmedAssetOperation-orphaned',
          newImageId: data.newImageId,
        });

        dispatch(
          addImageAction({
            id: data.newImageId,
            url: data.newImageUrl,
            name: originalImage?.name || `Trimmed Image ${data.originalImageId}`,
            width: imageDimensions.width,
            height: imageDimensions.height,
            type: imageType,
            state: ImageState.UPLOADED,
            hasAssetDimensions: true,
          }),
        );
      } catch (error) {
        // Failed to add trimmed image to gallery
      }
      return;
    }

    try {
      // Load dimensions of the new trimmed image
      const imageDimensions = await loadImageDimensions(data.newImageUrl, undefined, {
        executor: 'setTrimmedAssetOperation',
        originalImageId: data.originalImageId,
        newImageId: data.newImageId,
      });

      batch(() => {
        // 1. Add the trimmed image to gallery
        dispatch(
          addImageAction({
            id: data.newImageId,
            url: data.newImageUrl,
            name: originalImage?.name || elementsWithImageId[0].element.name, // Use original or first element name
            width: imageDimensions.width,
            height: imageDimensions.height,
            type: imageType,
            state: ImageState.UPLOADED,
            hasAssetDimensions: true,
          }),
        );

        // 2. Normalize trimmed image: resize frame to match trimmed image aspect ratio
        // This makes the frame and image equal (pw = width, ph = height), just like normal images
        // After normalization, the trimmed image behaves exactly like a normal image
        elementsWithImageId.forEach(({ element }) => {
          const trimmedAspectRatio = imageDimensions.width / imageDimensions.height;
          const currentFrameAspectRatio = element.width / element.height;

          // Calculate new frame dimensions to match trimmed image aspect ratio
          // Preserve the larger dimension to avoid making the frame too small
          let newWidth: number;
          let newHeight: number;

          if (trimmedAspectRatio > currentFrameAspectRatio) {
            // Trimmed image is wider - fit to width, adjust height
            newWidth = element.width;
            newHeight = element.width / trimmedAspectRatio;
          } else {
            // Trimmed image is taller - fit to height, adjust width
            newWidth = element.height * trimmedAspectRatio;
            newHeight = element.height;
          }

          const newImage = getImageById(getState(), data.newImageId);

          // Resize frame to match trimmed image, then image fills frame completely
          // This makes pw = width and ph = height (normalized, like a normal image)
          dispatch(
            updateMediaElementByUuidOperation(element.uuid, {
              imageId: data.newImageId,
              url: newImage?.url || data.newImageUrl,
              width: newWidth,
              height: newHeight,
              pw: newWidth, // Image fills frame completely
              ph: newHeight, // Image fills frame completely
              px: 0, // Centered (no offset needed since image = frame)
              py: 0, // Centered (no offset needed since image = frame)
              pr: 0,
            }),
          );
        });
      });
    } catch (error) {
      // Failed to load trimmed image dimensions
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

export default setTrimmedAssetOperation;
